"use client";

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import { loadScript, type PayPalScriptOptions } from "@paypal/paypal-js";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { cn, toPaymentAmount } from "@/lib/utils";
import { useBraintree } from "@/components/checkout/braintree-provider";
import { useLoadingOverlay } from "@/components/providers/CheckoutLoading";
import {
  createBraintreePayment,
  createBraintreePaymentMethod,
} from "@/networking/fast-checkout/braintree.api";
import { type CartResponse } from "@/networking/carts.api";
import { updateAddress } from "@/networking/addresses.api";
import { updateShippingMethod } from "@/networking/shipping.api";

// Venmo cancel codes from the Braintree SDK
const VENMO_CANCEL_CODES = new Set([
  "VENMO_CANCELED",
  "VENMO_DESKTOP_CANCELED",
  "VENMO_MOBILE_POLLING_TOKENIZATION_CANCELED",
]);

// Timeout for the overall Venmo payment flow (API calls after tokenize)
const VENMO_FLOW_TIMEOUT_MS = 60_000;

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out. Please try again.`)),
      ms,
    );
    promise.then(
      (val) => {
        clearTimeout(timer);
        resolve(val);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

// ========== SKELETON ==========
const BraintreeButtonSkeleton = ({ className }: { className?: string }) => (
  <div className={cn("h-input w-full overflow-hidden rounded", className)}>
    <div className="h-full w-full animate-pulse rounded bg-linear-to-r from-gray-200 via-gray-300 to-gray-200" />
  </div>
);

// ========== SHARED SHIPPING CHANGE HANDLER ==========
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal SDK types
async function handleBraintreeShippingChange(
  data: any,
  paypalCheckoutInstance: any,
  cartToken: string,
  currency: string,
) {
  const shippingAddr = data.shipping_address || data.shippingAddress;
  const selectedOption =
    data.selected_shipping_option || data.selectedShippingOption;

  let updatedCartData: CartResponse | undefined;

  if (selectedOption?.id) {
    updatedCartData = await updateShippingMethod({
      cartToken,
      shippingMethodId: selectedOption.id,
    });
  } else if (shippingAddr) {
    updatedCartData = await updateAddress(
      {
        address: {
          name: "",
          address1: "",
          address2: "",
          city: shippingAddr.city || "",
          state: shippingAddr.state || "",
          postal_code:
            shippingAddr.postal_code || shippingAddr.postalCode || "",
          country_code:
            shippingAddr.country_code || shippingAddr.countryCode || "US",
        },
        type: "ship_to",
      },
      cartToken,
    );
  }

  const updatedCart = updatedCartData?.cart;
  if (!updatedCart) {
    throw new Error("Cart data unavailable after shipping update");
  }

  const selectedMethodId =
    selectedOption?.id || updatedCart.shipping_method?.id;

  const newAmount = toPaymentAmount(
    updatedCart.amount_total,
    updatedCart.currency_decimal_places,
  );

  // Zero shipping option prices when free shipping covers the full rate,
  // otherwise, when amount_total < shipping option amount, PayPal POST request to `patch_payment_resource` 422s(with
  // error message `"The requested action could not be performed, semantically incorrect, or failed business validation.`)
  const hasFreeShipping =
    (updatedCart.shipping_discount ?? 0) > 0 &&
    (updatedCart.shipping_discount ?? 0) >= (updatedCart.shipping_total ?? 0);

  const shippingOptions = (updatedCart.available_shipping_methods || []).map(
    (method, index) => ({
      id: method.id,
      type: "SHIPPING" as const,
      label: method.title,
      selected: selectedMethodId ? method.id === selectedMethodId : index === 0,
      amount: {
        value: hasFreeShipping
          ? "0.00"
          : toPaymentAmount(method.price, updatedCart.currency_decimal_places),
        currency: updatedCart.currency_code || currency,
      },
    }),
  );

  return paypalCheckoutInstance.updatePayment({
    paymentId: data.paymentId,
    amount: newAmount,
    currency: updatedCart.currency_code || currency,
    ...(shippingOptions.length > 0 && { shippingOptions }),
  });
}

// ========== PAYPAL BUTTON RENDERER ==========
const BraintreePayPalRenderer = ({
  cartToken,
  amount,
  currency,
  cart,
  disabled,
  onSuccess,
  onError,
  fastCheckout,
  className,
}: {
  cartToken: string;
  amount: string;
  currency: string;
  cart: CartResponse["cart"] | undefined;
  disabled: boolean;
  onSuccess: (result: {
    transaction_id: string;
    payment_type: string;
  }) => void | Promise<void>;
  onError: (error: string) => void;
  fastCheckout?: boolean;
  className?: string;
}) => {
  const {
    isReady,
    paypalCheckoutInstance,
    deviceData,
    braintreePaymentAccountId,
  } = useBraintree();
  const { showCheckoutLoader, hideCheckoutLoader } = useLoadingOverlay();
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal Buttons instance
  const buttonsInstanceRef = useRef<any>(null);
  const [isRendered, setIsRendered] = useState(false);

  const hasSubscription = !!cart?.items?.some((item) => item.subscription);
  const prevHasSubscriptionRef = useRef(hasSubscription);

  // Reset rendered state when subscription status changes so the PayPal
  // button re-initializes with the correct flow (vault vs checkout).
  // Skip the initial mount — only react to actual changes.
  useEffect(() => {
    if (prevHasSubscriptionRef.current === hasSubscription) return;
    prevHasSubscriptionRef.current = hasSubscription;

    setIsRendered(false);
    if (buttonsInstanceRef.current) {
      buttonsInstanceRef.current.close();
      buttonsInstanceRef.current = null;
    }
    if (containerRef.current) containerRef.current.innerHTML = "";
  }, [hasSubscription]);

  const handleCreateOrder = useEffectEvent(() => {
    const subscriptionItems = cart?.items?.filter((item) => item.subscription);

    if (subscriptionItems && subscriptionItems.length > 0) {
      const description = subscriptionItems
        .map((item) => {
          const plan = item.subscription_plans?.find(
            (p) => p.id === item.subscription_plan_id,
          );
          const price =
            item.subscription_price_in_currency ?? item.price_in_currency;
          const interval = plan
            ? `${plan.billing_interval} ${plan.billing_interval_unit}`
            : item.subscription_interval && item.subscription_interval_unit
              ? `${item.subscription_interval} ${item.subscription_interval_unit}`
              : "recurring";
          return `${item.product_title} (${price}/${interval})`;
        })
        .join(" + ");

      const fullDescription = `Recurring: ${description}`;
      const billingAgreementDescription =
        fullDescription.length > 255
          ? fullDescription.slice(0, 252) + "..."
          : fullDescription;

      return paypalCheckoutInstance.createPayment({
        flow: "vault",
        billingAgreementDescription,
        enableShippingAddress: true,
      });
    }

    return paypalCheckoutInstance.createPayment({
      flow: "checkout",
      amount,
      currency,
      intent: "authorize",
      enableShippingAddress: true,
    });
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal SDK callback data
  const handleApprove = useEffectEvent(async (data: any) => {
    try {
      showCheckoutLoader("Processing your payment...");
      const payload = await paypalCheckoutInstance.tokenizePayment(data);

      const paypalShipping = payload.details?.shippingAddress;

      // For fast checkout vault flow only: update cart with PayPal address and
      // auto-select shipping before creating the payment — the vault modal
      // doesn't have onShippingChange so the cart hasn't been updated yet.
      // Non-fast-checkout (guest/logged-in) intentionally skipped: the user
      // fills in their address on the checkout form, which is authoritative.
      // Overwriting it with PayPal's stored address could lose details.
      if (fastCheckout && hasSubscription && paypalShipping) {
        const addr = {
          address: {
            name: [
              payload.details?.firstName?.trim(),
              payload.details?.lastName?.trim(),
            ]
              .filter(Boolean)
              .join(" "),
            address1: paypalShipping.line1 || "",
            address2: paypalShipping.line2 || "",
            city: paypalShipping.city || "",
            state: paypalShipping.state || "",
            postal_code: paypalShipping.postalCode || "",
            country_code: paypalShipping.countryCode || "US",
          },
          type: "ship_to" as const,
        };

        const updatedCartData = await updateAddress(addr, cartToken);

        // Also update billing address
        await updateAddress({ ...addr, type: "bill_to" }, cartToken);

        // Auto-select first shipping method if none selected
        if (
          !updatedCartData?.cart?.digital_only &&
          !updatedCartData?.cart?.shipping_method
        ) {
          const firstMethod =
            updatedCartData?.cart?.available_shipping_methods?.[0];
          if (firstMethod) {
            await updateShippingMethod({
              cartToken,
              shippingMethodId: firstMethod.id,
            });
          }
        }

        // Update email on cart if available
        if (payload.details?.email) {
          const { updateCartAttributes } =
            await import("@/networking/carts.api");
          await updateCartAttributes({
            cartToken,
            email: payload.details.email,
          }).catch(() => {});
        }
      }

      const result = await createBraintreePayment(cartToken, {
        payment_method_nonce: payload.nonce,
        device_data: deviceData || undefined,
        payment_account_id: braintreePaymentAccountId,
        payer_email: payload.details?.email,
        shipping_address: paypalShipping
          ? {
              recipient_name: paypalShipping.recipientName,
              line1: paypalShipping.line1,
              line2: paypalShipping.line2,
              city: paypalShipping.city,
              state: paypalShipping.state,
              postal_code: paypalShipping.postalCode,
              country_code: paypalShipping.countryCode,
            }
          : undefined,
      });

      await createBraintreePaymentMethod({
        cartToken,
        transactionId: result.transaction_id,
      });

      await onSuccess(result);
    } catch (err) {
      hideCheckoutLoader();
      console.error("[Braintree PayPal] Payment failed:", err);
      onError(err instanceof Error ? err.message : "PayPal payment failed");
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal SDK error
  const handleButtonError = useEffectEvent((err: any) => {
    onError(err?.message || "PayPal button error");
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal onShippingChange data
  const handleShippingChange = useEffectEvent(async (data: any) => {
    try {
      return await handleBraintreeShippingChange(
        data,
        paypalCheckoutInstance,
        cartToken,
        currency,
      );
    } catch (err) {
      console.error("[Braintree PayPal] Shipping change failed:", err);
      throw err;
    }
  });

  useEffect(() => {
    if (
      !isReady ||
      !paypalCheckoutInstance ||
      !containerRef.current ||
      isRendered
    )
      return;

    let cancelled = false;

    async function render() {
      try {
        const clientId = await paypalCheckoutInstance.getClientId();
        if (cancelled || !containerRef.current) return;

        const scriptOptions: PayPalScriptOptions = {
          clientId,
          currency,
          ...(!hasSubscription && { intent: "authorize" }),
          vault: hasSubscription ? "true" : undefined,
          components: "buttons,funding-eligibility,messages",
          disableFunding: "venmo",
          enableFunding: "paylater",
          dataNamespace: "paypal_sdk",
        };

        if (process.env.NODE_ENV === "development") {
          scriptOptions.buyerCountry = "US";
        }

        const paypal = await loadScript(scriptOptions);

        if (cancelled || !paypal?.Buttons || !containerRef.current) {
          if (!cancelled) {
            console.warn(
              "[Braintree PayPal] SDK loaded but Buttons unavailable; degrading silently.",
            );
          }
          return;
        }

        const buttonConfig: Record<string, unknown> = {
          fundingSource: paypal.FUNDING?.PAYPAL,
          style: {
            shape: "rect",
            layout: "vertical",
            height: 48,
            ...(fastCheckout && { color: "gold", tagline: false }),
          },
          onApprove: handleApprove,
          onCancel: () => {},
          onError: handleButtonError,
          // onShippingChange only applies to checkout flow — vault flow
          // doesn't show shipping in the modal and has no paymentId.
          ...(!hasSubscription && { onShippingChange: handleShippingChange }),
        };

        if (hasSubscription) {
          buttonConfig.createBillingAgreement = handleCreateOrder;
        } else {
          buttonConfig.createOrder = handleCreateOrder;
        }

        const buttonsInstance = paypal.Buttons(buttonConfig);
        buttonsInstanceRef.current = buttonsInstance;
        await buttonsInstance.render(containerRef.current);

        if (!cancelled) setIsRendered(true);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load PayPal SDK:", err);
        }
      }
    }

    render();
    return () => {
      cancelled = true;
    };
  }, [
    isReady,
    paypalCheckoutInstance,
    currency,
    isRendered,
    fastCheckout,
    hasSubscription,
  ]);

  if (!isReady) return <BraintreeButtonSkeleton className={className} />;

  return (
    <div
      ref={containerRef}
      style={!isRendered ? { minHeight: 48 } : undefined}
      className={cn(
        className,
        disabled && "pointer-events-none cursor-not-allowed opacity-50",
      )}
    />
  );
};

// ========== PAY LATER BUTTON RENDERER ==========
const BraintreePayLaterRenderer = ({
  cartToken,
  amount,
  currency,
  disabled,
  onSuccess,
  onError,
  className,
}: {
  cartToken: string;
  amount: string;
  currency: string;
  disabled: boolean;
  onSuccess: (result: {
    transaction_id: string;
    payment_type: string;
  }) => void | Promise<void>;
  onError: (error: string) => void;
  className?: string;
}) => {
  const {
    isReady,
    paypalCheckoutInstance,
    deviceData,
    braintreePaymentAccountId,
  } = useBraintree();
  const { showCheckoutLoader, hideCheckoutLoader } = useLoadingOverlay();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasRenderedRef = useRef(false);
  const [isRendered, setIsRendered] = useState(false);
  const [isEligible, setIsEligible] = useState<boolean | null>(null);

  const handleCreateOrder = useEffectEvent(() => {
    return paypalCheckoutInstance.createPayment({
      flow: "checkout",
      amount,
      currency,
      intent: "authorize",
      enableShippingAddress: true,
    });
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal SDK callback data
  const handleApprove = useEffectEvent(async (data: any) => {
    try {
      showCheckoutLoader("Processing your payment...");
      const payload = await paypalCheckoutInstance.tokenizePayment(data);

      const paypalShipping = payload.details?.shippingAddress;

      const result = await createBraintreePayment(cartToken, {
        payment_method_nonce: payload.nonce,
        device_data: deviceData || undefined,
        payment_account_id: braintreePaymentAccountId,
        payer_email: payload.details?.email,
        shipping_address: paypalShipping
          ? {
              recipient_name: paypalShipping.recipientName,
              line1: paypalShipping.line1,
              line2: paypalShipping.line2,
              city: paypalShipping.city,
              state: paypalShipping.state,
              postal_code: paypalShipping.postalCode,
              country_code: paypalShipping.countryCode,
            }
          : undefined,
      });

      await createBraintreePaymentMethod({
        cartToken,
        transactionId: result.transaction_id,
      });

      await onSuccess(result);
    } catch (err) {
      hideCheckoutLoader();
      console.error("[Braintree PayLater] Payment failed:", err);
      onError(err instanceof Error ? err.message : "Pay Later payment failed");
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal SDK error
  const handleButtonError = useEffectEvent((err: any) => {
    onError(err?.message || "Pay Later button error");
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal onShippingChange data
  const handleShippingChange = useEffectEvent(async (data: any) => {
    try {
      return await handleBraintreeShippingChange(
        data,
        paypalCheckoutInstance,
        cartToken,
        currency,
      );
    } catch (err) {
      console.error("[Braintree PayLater] Shipping change failed:", err);
      throw err;
    }
  });

  useEffect(() => {
    if (
      !isReady ||
      !paypalCheckoutInstance ||
      !containerRef.current ||
      hasRenderedRef.current
    )
      return;

    let cancelled = false;

    async function render() {
      try {
        if (cancelled || !containerRef.current) return;

        // Wait for the PayPal SDK loaded by BraintreePayPalRenderer
        // instead of calling loadScript ourselves (avoids race condition)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal SDK on window
        const windowObj = window as any;
        const maxAttempts = 50; // 5 seconds max
        let paypal = windowObj.paypal_sdk;

        for (let i = 0; !paypal?.Buttons && i < maxAttempts; i++) {
          await new Promise((r) => setTimeout(r, 100));
          if (cancelled) return;
          paypal = windowObj.paypal_sdk;
        }

        if (!paypal?.Buttons || !containerRef.current) {
          // BraintreePayPalRenderer already reports SDK load failures —
          // silently bail to avoid duplicate error messages.
          if (!cancelled) setIsEligible(false);
          return;
        }

        const buttons = paypal.Buttons({
          fundingSource: paypal.FUNDING?.PAYLATER,
          style: {
            shape: "rect",
            layout: "vertical",
            height: 48,
            color: "white",
            tagline: false,
          },
          createOrder: handleCreateOrder,
          onApprove: handleApprove,
          onCancel: () => {},
          onError: handleButtonError,
          onShippingChange: handleShippingChange,
        });

        if (!buttons.isEligible()) {
          if (!cancelled) setIsEligible(false);
          return;
        }

        if (!cancelled) {
          setIsEligible(true);
          await buttons.render(containerRef.current!);
          hasRenderedRef.current = true;
          setIsRendered(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load PayPal SDK for Pay Later:", err);
          setIsEligible(false);
        }
      }
    }

    render();
    return () => {
      cancelled = true;
    };
  }, [isReady, paypalCheckoutInstance]);

  if (isEligible === false) return null;

  const isLoading = !isReady || (!isRendered && isEligible === null);

  return (
    <>
      {isLoading && <BraintreeButtonSkeleton className={className} />}
      <div
        ref={containerRef}
        className={cn(
          isLoading && "opacity-0",
          className,
          disabled && "pointer-events-none cursor-not-allowed opacity-50",
        )}
      />
    </>
  );
};

// ========== PAY LATER MESSAGING ==========
// NOTE: This component requires BraintreePayPalRenderer to be mounted on the
// same page, as it depends on BraintreePayPalRenderer having loaded
// `window.paypal_sdk` with the `messages` component included.
const BraintreePayLaterMessage = ({
  amount,
  currency,
  className,
}: {
  amount: string;
  currency: string;
  className?: string;
}) => {
  const { isReady, paypalCheckoutInstance } = useBraintree();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isEligible, setIsEligible] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isReady || !paypalCheckoutInstance || !containerRef.current) return;

    if (containerRef.current) containerRef.current.innerHTML = "";

    let cancelled = false;

    async function render() {
      if (cancelled || !containerRef.current) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PayPal SDK on window
      const windowObj = window as any;
      const maxAttempts = 50;
      let paypal = windowObj.paypal_sdk;

      for (let i = 0; !paypal?.Messages && i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 100));
        if (cancelled) return;
        paypal = windowObj.paypal_sdk;
      }

      if (!paypal?.Messages || !containerRef.current || cancelled) {
        if (!cancelled) setIsEligible(false);
        return;
      }

      try {
        await paypal
          .Messages({
            amount: Number(amount),
            currency,
            style: {
              layout: "text",
              logo: { type: "primary", position: "left" },
              text: { size: 12 },
            },
          })
          .render(containerRef.current);
        if (!cancelled) setIsEligible(true);
      } catch (err) {
        console.error("[Braintree PayLater] Messaging render failed:", err);
        if (!cancelled) setIsEligible(false);
      }
    }

    render();
    return () => {
      cancelled = true;
    };
  }, [isReady, paypalCheckoutInstance, amount, currency]);

  if (isEligible === false) return null;

  return <div ref={containerRef} className={className} />;
};

const VenmoLogo = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 198 58"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Venmo"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M43.07 13.66c1.12 1.8 1.63 3.66 1.63 6 0 7.48-6.57 17.19-11.91 24.01H20.61l-4.89-28.37 10.67-.98 2.58 20.18c2.41-3.82 5.39-9.82 5.39-13.91 0-2.24-.4-3.77-1.01-5.02l9.71-1.91z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M56.9 26.15c1.96 0 6.9-.87 6.9-3.6 0-1.31-.95-1.96-2.08-1.96-1.97 0-4.55 2.29-4.83 5.56zm-.23 5.4c0 3.33 1.91 4.64 4.44 4.64 2.75 0 5.39-.65 8.81-2.35l-1.29 8.51c-2.41 1.15-6.18 1.91-9.83 1.91-9.26 0-12.58-5.46-12.58-12.27 0-8.84 5.39-18.22 16.51-18.22 6.12 0 9.55 3.33 9.55 7.97 0 7.47-9.88 9.76-15.61 9.81z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M103.07 20.31c0 1.1-.17 2.67-.34 3.71l-3.2 19.64H89.14l2.92-18 .23-2.02c0-1.31-.84-1.64-1.86-1.64-1.34 0-2.69.6-3.59 1.04l-3.31 20.62H73.08l4.77-29.41h8.94l.12 2.35c2.13-1.37 4.94-2.84 8.93-2.84 5.28 0 7.13 2.62 7.13 6.55z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M133.91 16.98c2.97-2.07 5.78-3.22 9.65-3.22 5.33 0 7.19 2.62 7.19 6.55 0 1.1-.17 2.67-.34 3.71l-3.2 19.64h-10.39l2.98-18.38c.05-.49.17-1.09.17-1.47 0-1.47-.84-1.8-1.86-1.8-1.29 0-2.58.55-3.54 1.04l-3.31 20.62h-10.39l2.98-18.38c.05-.49.16-1.09.16-1.47 0-1.47-.84-1.8-1.85-1.8-1.35 0-2.69.6-3.59 1.04l-3.31 20.62h-10.44l4.77-29.41h8.93l.28 2.45c2.08-1.47 4.89-2.95 8.65-2.95 3.26 0 5.39 1.36 6.46 3.22z"
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M171.43 25.55c0-2.4-.62-4.04-2.47-4.04-4.1 0-4.94 7.04-4.94 10.64 0 2.73.79 4.42 2.64 4.42 3.87 0 4.77-7.42 4.77-11.02zm-17.97 6.17c0-9.27 5.05-17.95 16.68-17.95 8.76 0 11.96 5.02 11.96 11.95 0 9.17-5 18.66-16.9 18.66-8.82 0-11.74-5.62-11.74-12.66z"
      fill="white"
    />
  </svg>
);

// ========== VENMO BUTTON RENDERER ==========
const BraintreeVenmoRenderer = ({
  cartToken,
  disabled,
  fastCheckout,
  cart,
  onSuccess,
  onError,
  className,
}: {
  cartToken: string;
  disabled: boolean;
  fastCheckout?: boolean;
  cart?: CartResponse["cart"];
  onSuccess: (result: {
    transaction_id: string;
    payment_type: string;
  }) => void | Promise<void>;
  onError: (error: string) => void;
  className?: string;
}) => {
  const { isReady, venmoInstance, deviceData, braintreePaymentAccountId } =
    useBraintree();
  const { showCheckoutLoader, hideCheckoutLoader } = useLoadingOverlay();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleClick = useCallback(async () => {
    if (!venmoInstance || isProcessing) return;

    setIsProcessing(true);
    try {
      const payload = await venmoInstance.tokenize();
      showCheckoutLoader("Processing your payment...");

      const payerInfo = payload.details?.payerInfo;
      const venmoShipping = payerInfo?.shippingAddress;

      // In fast checkout, Venmo has no onShippingChange callback so the cart
      // may not have a shipping address yet. If Venmo returned one, use it to
      // update the cart before authorizing payment. If neither Venmo nor the
      // cart has an address, block — we can't charge without knowing shipping.
      if (fastCheckout) {
        const cartHasAddress = !!cart?.ship_to?.address1;

        if (!cart?.digital_only && !venmoShipping && !cartHasAddress) {
          hideCheckoutLoader();
          onError(
            "No shipping address found on your Venmo account. Please complete checkout below to enter your shipping address.",
          );
          return;
        }
      }

      // Track whether we updated the cart from Venmo's address so we know
      // which address to pass to createBraintreePayment below.
      let updatedCartFromVenmo = false;

      // Single timeout for the entire post-tokenize flow to ensure the
      // spinner never stays visible for more than 60 seconds total.
      await withTimeout(
        (async () => {
          if (
            fastCheckout &&
            venmoShipping &&
            !cart?.ship_to?.address1 &&
            !cart?.digital_only
          ) {
            const addr = {
              address: {
                name: venmoShipping.fullName || "",
                address1: venmoShipping.addressLine1 || "",
                address2: venmoShipping.addressLine2 || "",
                city: venmoShipping.adminArea2 || "",
                state: venmoShipping.adminArea1 || "",
                postal_code: venmoShipping.postalCode || "",
                country_code: venmoShipping.countryCode || "US",
              },
              type: "ship_to" as const,
            };

            // Update ship_to and bill_to in parallel — only ship_to
            // response is needed downstream for shipping method selection.
            const [updatedCartData] = await Promise.all([
              updateAddress(addr, cartToken),
              updateAddress({ ...addr, type: "bill_to" }, cartToken),
            ]);

            // Auto-select first shipping method if none selected
            if (
              !updatedCartData?.cart?.digital_only &&
              !updatedCartData?.cart?.shipping_method
            ) {
              const firstMethod =
                updatedCartData?.cart?.available_shipping_methods?.[0];
              if (firstMethod) {
                await updateShippingMethod({
                  cartToken,
                  shippingMethodId: firstMethod.id,
                });
              } else if (!updatedCartData?.cart?.digital_only) {
                throw new Error(
                  "No shipping methods available for your Venmo address. Please use a different address or complete checkout below.",
                );
              }
            }

            updatedCartFromVenmo = true;
          }

          // Only pass Venmo's shipping address to Braintree if we used it
          // to update the cart. If the cart already had an address, the cart
          // address is the source of truth — avoid a mismatch.
          const result = await createBraintreePayment(cartToken, {
            payment_method_nonce: payload.nonce,
            device_data: deviceData || undefined,
            payment_account_id: braintreePaymentAccountId,
            payer_email: payerInfo?.email,
            shipping_address:
              venmoShipping && (!fastCheckout || updatedCartFromVenmo)
                ? {
                    recipient_name: venmoShipping.fullName,
                    line1: venmoShipping.addressLine1,
                    line2: venmoShipping.addressLine2,
                    city: venmoShipping.adminArea2,
                    state: venmoShipping.adminArea1,
                    postal_code: venmoShipping.postalCode,
                    country_code: venmoShipping.countryCode,
                  }
                : undefined,
          });

          await createBraintreePaymentMethod({
            cartToken,
            transactionId: result.transaction_id,
          });

          await onSuccess(result);
        })(),
        VENMO_FLOW_TIMEOUT_MS,
        "Venmo payment",
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Braintree Venmo SDK error
    } catch (err: any) {
      hideCheckoutLoader();
      if (VENMO_CANCEL_CODES.has(err?.code)) {
        return;
      }
      console.error("[Venmo] Payment failed:", err?.code, err?.message, err);
      onError(err?.message || "Venmo payment failed");
    } finally {
      setIsProcessing(false);
    }
  }, [
    venmoInstance,
    isProcessing,
    fastCheckout,
    cart,
    cartToken,
    deviceData,
    braintreePaymentAccountId,
    onSuccess,
    onError,
    showCheckoutLoader,
    hideCheckoutLoader,
  ]);

  if (!isReady || !venmoInstance)
    return <BraintreeButtonSkeleton className={className} />;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isProcessing}
      className={cn(
        "h-input flex w-full items-center justify-center rounded border-none bg-[#008CFF] px-6 text-base font-semibold text-white",
        disabled || isProcessing
          ? "cursor-not-allowed opacity-50"
          : "cursor-pointer",
        className,
      )}
    >
      {isProcessing ? "Processing..." : <VenmoLogo className="h-5" />}
    </button>
  );
};

// ========== MAIN HOOK ==========
interface UseBraintreePaymentProps {
  fastCheckout?: boolean;
  cartToken?: string;
  disabled?: boolean;
  onSuccess?: (result: {
    transaction_id: string;
    payment_type: string;
  }) => void | Promise<void>;
  onError?: (error: string) => void;
  paymentMethodsIncludes?: string | null;
}

export default function useBraintreePayment(props: UseBraintreePaymentProps) {
  const {
    fastCheckout,
    cartToken,
    disabled,
    onSuccess,
    onError,
    paymentMethodsIncludes,
  } = props;
  const { isReady, shouldShowPayPal, shouldShowVenmo } = useBraintree();

  const queryClient = useQueryClient();
  const cartData = queryClient.getQueryData<CartResponse>([
    "cart",
    cartToken,
    paymentMethodsIncludes,
  ]);
  const amount = String(cartData?.cart?.amount_total ?? "0");
  const currency = cartData?.cart?.currency_code ?? "USD";
  const hasSubscription = !!cartData?.cart?.items?.some(
    (item) => item.subscription,
  );
  const isUSA =
    cartData?.cart?.country?.iso === "US" ||
    cartData?.cart?.country?.iso === "USA";

  const isSubscriptionPending =
    useIsMutating({ mutationKey: ["update-item-subscription", cartToken] }) > 0;
  const isQuantityPending =
    useIsMutating({ mutationKey: ["update-item-quantity", cartToken] }) > 0;
  const isDiscountPending =
    useIsMutating({ mutationKey: ["apply-discount", cartToken] }) > 0;
  const isRemoveDiscountPending =
    useIsMutating({ mutationKey: ["remove-discount", cartToken] }) > 0;

  const isCartMutating =
    isSubscriptionPending ||
    isQuantityPending ||
    isDiscountPending ||
    isRemoveDiscountPending;

  const isDisabled = isCartMutating || !!disabled;

  const showVenmo = shouldShowVenmo && !hasSubscription && isUSA;
  // Pay Later eligibility is determined at render time by the PayPal SDK's
  // isEligible() check inside BraintreePayLaterRenderer. We only gate on
  // the business rule (no subscriptions) here.
  const showPayLater = shouldShowPayPal && !hasSubscription;

  if (!cartToken || !onSuccess || !onError) {
    return {
      paypalButton: null,
      venmoButton: null,
      payLaterButton: null,
      payLaterMessage: null,
    };
  }

  if (isReady && !shouldShowPayPal) {
    return {
      paypalButton: null,
      venmoButton: showVenmo ? (
        <BraintreeVenmoRenderer
          cartToken={cartToken}
          disabled={isDisabled}
          fastCheckout={fastCheckout}
          cart={cartData?.cart}
          onSuccess={onSuccess}
          onError={onError}
          className={cn(!fastCheckout && "mt-3")}
        />
      ) : null,
      payLaterButton: null,
      payLaterMessage: null,
    };
  }

  if (!isReady) {
    return {
      paypalButton: (
        <BraintreeButtonSkeleton
          className={cn(!fastCheckout && "mt-6 -mb-1.5")}
        />
      ),
      venmoButton: showVenmo ? (
        <BraintreeButtonSkeleton className={cn(!fastCheckout && "mt-3")} />
      ) : null,
      payLaterButton: showPayLater ? (
        <BraintreeButtonSkeleton className={cn(!fastCheckout && "mt-3")} />
      ) : null,
      payLaterMessage: showPayLater ? (
        <BraintreePayLaterMessage amount={amount} currency={currency} />
      ) : null,
    };
  }

  return {
    paypalButton: (
      <BraintreePayPalRenderer
        cartToken={cartToken}
        amount={amount}
        currency={currency}
        cart={cartData?.cart}
        disabled={isDisabled}
        onSuccess={onSuccess}
        onError={onError}
        fastCheckout={fastCheckout}
        className={cn(!fastCheckout && "mt-6 -mb-1.5")}
      />
    ),
    venmoButton: showVenmo ? (
      <BraintreeVenmoRenderer
        cartToken={cartToken}
        disabled={isDisabled}
        fastCheckout={fastCheckout}
        cart={cartData?.cart}
        onSuccess={onSuccess}
        onError={onError}
        className={cn(!fastCheckout && "mt-3")}
      />
    ) : null,
    payLaterButton: showPayLater ? (
      <BraintreePayLaterRenderer
        cartToken={cartToken}
        amount={amount}
        currency={currency}
        disabled={isDisabled}
        onSuccess={onSuccess}
        onError={onError}
        className={cn(!fastCheckout && "mt-3")}
      />
    ) : null,
    payLaterMessage: showPayLater ? (
      <BraintreePayLaterMessage amount={amount} currency={currency} />
    ) : null,
  };
}
